import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { getTokenFromStorage } from '@/utils/auth';
import {
  getCampaignsSearch,
  type ClientCampaignsSearchResponse,
  type ClientCampaignSortBy,
  type GetCampaignsSearchParams,
} from '@/api/beatlyApi';

/** Filters supported server-side (beatly-api-v2 GET .../campaigns/search). */
export type CampaignsListFilters = Pick<
  GetCampaignsSearchParams,
  'search' | 'brandName' | 'brandId' | 'listStatus' | 'budgetFilter' | 'sortBy' | 'platform'
>;

export interface UseCampaignsParams {
  pageNumber?: number;
  pageSize?: number;
  /**
   * Client team id (must match a team the user belongs to). Same as Settings / `user.team`.
   * Omit or empty string disables the query until a team id is known.
   */
  teamScope?: string;
  /** Pass-through to API; omit fields for no filter. */
  filters?: CampaignsListFilters;
}

function filtersKey(f: CampaignsListFilters | undefined): string {
  if (!f || Object.keys(f).length === 0) {
    return '';
  }
  return JSON.stringify({
    search: f.search ?? '',
    brandName: f.brandName ?? '',
    brandId: f.brandId ?? '',
    listStatus: f.listStatus ?? '',
    budgetFilter: f.budgetFilter ?? '',
    sortBy: f.sortBy ?? 'newest',
    platform: f.platform ?? '',
  });
}

/**
 * Fetches paginated campaigns for the current client.
 * Same API as beatly-web-app: GET /app/v2/client/campaigns/search
 */
export function useCampaigns(params: UseCampaignsParams = {}) {
  const { pageNumber = 0, pageSize = 10, teamScope = '', filters = {} } = params;
  const sortBy: ClientCampaignSortBy = filters.sortBy ?? 'newest';
  const { user } = useAuth();
  const token = getTokenFromStorage();

  const specificTeamId = teamScope.trim();

  const enabled = Boolean(token && user?.is_client && specificTeamId);

  const fKey = filtersKey(filters);

  const queryKey = [
    'client',
    'campaigns',
    'search',
    specificTeamId,
    pageNumber,
    pageSize,
    fKey,
  ];

  const searchPayload = useMemo(
    (): Pick<
      GetCampaignsSearchParams,
      | 'search'
      | 'brandName'
      | 'brandId'
      | 'listStatus'
      | 'budgetFilter'
      | 'sortBy'
      | 'platform'
      | 'teamId'
    > => ({
      search: filters.search,
      brandName: filters.brandName,
      brandId: filters.brandId,
      listStatus: filters.listStatus,
      budgetFilter: filters.budgetFilter,
      sortBy,
      platform: filters.platform,
      teamId: specificTeamId,
    }),
    [
      filters.search,
      filters.brandName,
      filters.brandId,
      filters.listStatus,
      filters.budgetFilter,
      filters.platform,
      sortBy,
      specificTeamId,
    ],
  );

  const { data, isLoading, error, isError } = useQuery({
    queryKey,
    queryFn: async (): Promise<ClientCampaignsSearchResponse> =>
      getCampaignsSearch(
        {
          pageNumber,
          pageSize,
          ...searchPayload,
        },
        token ?? '',
      ),
    enabled,
  });

  return {
    data: data ?? {
      campaigns: [],
      page_number: 0,
      page_size: pageSize,
      count_total: 0,
      has_more: false,
    },
    isLoading,
    error,
    isError,
    isEnabled: enabled,
  };
}
